import React, { useState } from 'react'

import {
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    CircularProgress,
} from '@material-ui/core'
import CloudUploadIcon from '@material-ui/icons/CloudUpload'
import CloseIcon from '@material-ui/icons/Close'
import CheckIcon from '@material-ui/icons/Check'

import { Container } from '../../components'
import {
    Damage,
    DamageStatus,
    getDamageTransitions,
    WorkSpaceUser,
} from '../../models'

interface Props {
    workspaceUser?: WorkSpaceUser
    damages?: Damage[] | Damage
    /** When bulk-selecting across pages, use the same damage row the list uses for per-page selection (e.g. first visible row) so transitions match the checkbox toolbar. */
    transitionDamage?: Damage
    handleStatusChange: (
        damages: Damage[],
        status: DamageStatus,
    ) => Promise<void> | void | undefined
}

export const DamageStatusChangeButtons = (props: Props) => {
    const { damages, workspaceUser, transitionDamage } = props
    const [openDenyDialog, setOpenDenyDialog] = useState(false)
    const [isSyncing, setIsSyncing] = useState(false)

    if (damages === undefined) {
        return null
    }

    let safeDamages: Damage[]
    if (!Array.isArray(damages)) {
        safeDamages = [damages]
    } else {
        safeDamages = damages
    }

    if (safeDamages.length === 0) {
        return null
    }

    const canonicalSource =
        transitionDamage !== undefined ? transitionDamage : safeDamages[0]

    if (transitionDamage !== undefined) {
        if (!safeDamages.every((d) => d.status === transitionDamage.status)) {
            return null
        }
    } else {
        const firstStatus = safeDamages[0].status
        for (let i = 0; i < safeDamages.length; i++) {
            if (firstStatus !== safeDamages[i].status) {
                return null
            }
        }
    }

    const transitions = getDamageTransitions(canonicalSource, workspaceUser)

    const isEntrataSync =
        transitions.positive?.to_status === DamageStatus.ENTRATA_SYNCED

    let PositionTransitionIcon = CheckIcon
    if (isEntrataSync) {
        PositionTransitionIcon = CloudUploadIcon
    }

    const handleDenyConfirm = () => {
        if (transitions.negative?.permission) {
            props.handleStatusChange(
                safeDamages,
                transitions.negative.to_status,
            )
        }
        setOpenDenyDialog(false)
    }

    return (
        <>
            <Container
                style={{
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    marginTop: 4,
                    marginBottom: 4,
                }}
            >
                {transitions.negative && (
                    <div>
                        <Tooltip
                            title={
                                !transitions.negative.permission
                                    ? 'Permission denied'
                                    : transitions.negative.title
                            }
                        >
                            <IconButton
                                onClick={() => {
                                    if (transitions.negative?.permission) {
                                        setOpenDenyDialog(true)
                                    }
                                }}
                                size="small"
                                style={{
                                    backgroundColor: '#f5f5f5',
                                    marginRight: 8,
                                    padding: 6,
                                }}
                            >
                                <CloseIcon fontSize="small" color="error" />
                            </IconButton>
                        </Tooltip>
                    </div>
                )}

                {transitions.positive && (
                    <div>
                        <Tooltip
                            title={
                                !transitions.positive.permission
                                    ? 'Permission denied'
                                    : transitions.positive.title
                            }
                        >
                            <IconButton
                                disabled={isEntrataSync && isSyncing}
                                onClick={async () => {
                                    if (
                                        !transitions.positive?.permission ||
                                        isSyncing
                                    ) {
                                        return
                                    }

                                    if (isEntrataSync) {
                                        setIsSyncing(true)
                                        try {
                                            await props.handleStatusChange(
                                                safeDamages,
                                                transitions.positive.to_status,
                                            )
                                        } finally {
                                            setIsSyncing(false)
                                        }
                                        return
                                    }

                                    props.handleStatusChange(
                                        safeDamages,
                                        transitions.positive.to_status,
                                    )
                                }}
                                size="small"
                                style={{
                                    backgroundColor: '#f5f5f5',
                                    padding: 6,
                                }}
                            >
                                {isEntrataSync && isSyncing ? (
                                    <CircularProgress size={18} />
                                ) : (
                                    <PositionTransitionIcon
                                        fontSize="small"
                                        color="primary"
                                    />
                                )}
                            </IconButton>
                        </Tooltip>
                    </div>
                )}
            </Container>

            <Dialog
                open={openDenyDialog}
                onClose={() => setOpenDenyDialog(false)}
            >
                <DialogTitle>Confirm Deny</DialogTitle>
                <DialogContent>
                    Are you sure you want to deny this damage?
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setOpenDenyDialog(false)}
                        color="primary"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDenyConfirm}
                        color="secondary"
                        variant="contained"
                    >
                        Deny
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}
